import React, { useState } from "react";

import { CloseOutlined } from "@ant-design/icons";
import { Button, Input, Modal } from "antd";

import SampleSubmissionConfirmModal from "../SampleSubmissionModal/SampleSubmissionConfirmModal";
import GroupsPanel from "./GroupsPanel";

import { canRequestTest } from "../../../utils/Permissions";
import getTestName, { getGroupTests } from "./helpers";

import "../MainPage/Submissions/ActionsMenu.css";
import "../SampleSubmissionModal/AddTestModal.css";
import "../SubmissionForm/SampleDetailsPage/AnalysisRequest/AnalysisRequest.css";
import "./TestDirectory.css";
import "./TestDirectoryModals.css";

export default function Groups({
  groups = [],
  saving = false,
  sampleCategory = "product",
  onCreate,
  onRename,
  onDelete,
  onUpdateTests,
}) {
  const canModifyGroups = canRequestTest();
  const [isCreating, setIsCreating] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftTests, setDraftTests] = useState([]);
  const [createError, setCreateError] = useState("");
  const [activeKey, setActiveKey] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [removeTestTarget, setRemoveTestTarget] = useState(null);

  const resetCreateDraft = () => {
    setDraftName("");
    setDraftTests([]);
    setCreateError("");
  };

  const startCreate = () => {
    if (!canModifyGroups || isCreating) return;
    resetCreateDraft();
    setActiveKey("draft");
    setIsCreating(true);
  };

  const cancelCreate = () => {
    if (saving) return;
    setIsCreating(false);
    setActiveKey(null);
    resetCreateDraft();
  };

  const addTestsToDraft = (testNames) => {
    const names = (Array.isArray(testNames) ? testNames : [testNames])
      .map(getTestName)
      .filter(Boolean);
    if (!names.length) return;
    setDraftTests((current) => {
      const next = [...current];
      names.forEach((testName) => {
        if (!next.includes(testName)) next.push(testName);
      });
      return next;
    });
    setCreateError("");
  };

  const removeDraftTest = (testName) => {
    setDraftTests((current) => current.filter((t) => t !== testName));
    setCreateError("");
  };

  const submitCreate = async () => {
    const name = draftName.trim();
    if (!name) {
      setCreateError("Group name is required");
      return;
    }
    if (!draftTests.length) {
      setCreateError("Add at least one test to the group");
      return;
    }
    if (typeof onCreate !== "function") return;
    const success = await onCreate({ group_name: name, test_list: draftTests });
    if (success !== false) {
      setIsCreating(false);
      setActiveKey(null);
      resetCreateDraft();
    }
  };

  const addTestsToGroup = (targetKey, testNames) => {
    if (!canModifyGroups) return;
    const names = (Array.isArray(testNames) ? testNames : [testNames])
      .map(getTestName)
      .filter(Boolean);
    if (!names.length || targetKey == null) return;

    const group = groups[Number(targetKey)];
    if (!group || typeof onUpdateTests !== "function") return;
    const current = getGroupTests(group).map(getTestName).filter(Boolean);
    const merged = [...current];
    names.forEach((testName) => {
      if (!merged.includes(testName)) merged.push(testName);
    });
    if (merged.length !== current.length) onUpdateTests(group, merged);
  };

  const removeTest = (group, testName) => {
    if (!canModifyGroups || typeof onUpdateTests !== "function") return;
    onUpdateTests(
      group,
      getGroupTests(group)
        .map(getTestName)
        .filter((name) => name && name !== testName),
    );
  };

  const submitRename = async () => {
    const nextName = renameValue.trim();
    if (!nextName || typeof onRename !== "function") return;
    const success = await onRename(renameTarget.group, nextName);
    if (success !== false) {
      setRenameTarget(null);
      setRenameValue("");
    }
  };

  return (
    <>
      <div className="TestDirectory__GroupsTab">
        <GroupsPanel
          sampleCategory={sampleCategory}
          groups={groups}
          canModifyGroups={canModifyGroups}
          activeKey={activeKey}
          onActiveKeyChange={setActiveKey}
          onNewGroupClick={isCreating ? undefined : startCreate}
          createDraft={isCreating ? {
            name: draftName,
            tests: draftTests,
            onNameChange: (value) => {
              setDraftName(value);
              setCreateError("");
            },
            onAddTests: addTestsToDraft,
            onRemoveTest: removeDraftTest,
            onSave: submitCreate,
            onCancel: cancelCreate,
            saving,
            error: createError,
          } : null}
          onAddTestsToGroup={addTestsToGroup}
          onRemoveTestFromGroup={({
            group, testName, groupName, key,
          }) => {
            setActiveKey(key);
            setRemoveTestTarget({ group, testName, groupName });
          }}
          onRenameRequest={({ group, name }) => {
            setRenameTarget({ group, name });
            setRenameValue(name);
          }}
          onDeleteRequest={({ group, name }) => {
            setDeleteTarget({ group, name });
          }}
        />
      </div>

      <Modal
        open={Boolean(renameTarget)}
        closable
        centered
        footer={null}
        width="452px"
        wrapClassName="TestDirectoryModal TestDirectoryModal--renameGroup"
        className="TestDirectoryModal TestDirectoryModal--renameGroup"
        destroyOnHidden
        closeIcon={<CloseOutlined className="SampleSubmissionModal__closeIcon" />}
        onCancel={() => setRenameTarget(null)}
      >
        <div className="AddTestTitle">Rename group</div>
        <div className="AddTestInput">
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder="Enter group name"
            onPressEnter={submitRename}
          />
        </div>
        <div className="AddTestBtn">
          <Button
            type="default"
            htmlType="button"
            onClick={() => setRenameTarget(null)}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="button"
            onClick={submitRename}
          >
            Save
          </Button>
        </div>
      </Modal>

      {deleteTarget && (
        <SampleSubmissionConfirmModal
          headerText="Delete group"
          bodyText={(
            <>
              Are you sure you want to delete
              {" "}
              <span className="sampleSubmissionConfirmDelTitleMessageHighlight">{deleteTarget.name}</span>
              ?
            </>
          )}
          buttonText={["Cancel", "Delete", "Deleting"]}
          buttonFunctions={[
            () => setDeleteTarget(null),
            async () => {
              if (typeof onDelete === "function") await onDelete(deleteTarget.group);
              setDeleteTarget(null);
            },
          ]}
        />
      )}

      {removeTestTarget && (
        <SampleSubmissionConfirmModal
          headerText="Remove test"
          bodyText={(
            <>
              Are you sure you want to remove
              {" "}
              <span className="sampleSubmissionConfirmDelTitleMessageHighlight">{removeTestTarget.testName}</span>
              {" from "}
              <span className="sampleSubmissionConfirmDelTitleMessageHighlight">{removeTestTarget.groupName}</span>
              ?
            </>
          )}
          buttonText={["Cancel", "Remove", "Removing"]}
          buttonFunctions={[
            () => setRemoveTestTarget(null),
            () => {
              removeTest(removeTestTarget.group, removeTestTarget.testName);
              setRemoveTestTarget(null);
            },
          ]}
        />
      )}
    </>
  );
}
